import { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '../utils/apiConfig';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ─── Service Worker Management ───────────────────────────────
let swReg = null;

async function ensureServiceWorker() {
  if (swReg) return swReg;
  if (!('serviceWorker' in navigator)) return null;

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) {
      if (reg.active && !reg.active.scriptURL.includes('sw-push.js')) {
        await reg.unregister();
      }
    }

    swReg = await navigator.serviceWorker.register('/sw-push.js', { scope: '/' });

    if (swReg.waiting) {
      swReg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    swReg.addEventListener('updatefound', () => {
      const newWorker = swReg.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated') {
            swReg = newWorker;
          }
        });
      }
    });

    await navigator.serviceWorker.ready;
    return swReg;
  } catch (err) {
    console.error('[Push] SW registration failed:', err);
    return null;
  }
}

// Register SW early on module import
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  ensureServiceWorker();
}

// ─── Fetch VAPID key from backend (cached) ───────────────────
let vapidKeyCache = null;

async function fetchVapidKey() {
  if (vapidKeyCache) return vapidKeyCache;

  // Try env var first
  const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
  if (envKey) {
    vapidKeyCache = envKey;
    return envKey;
  }

  // Fetch from backend
  try {
    const resp = await fetch(`${API}/push/vapid-key`);
    if (resp.ok) {
      const data = await resp.json();
      if (data.publicKey) {
        vapidKeyCache = data.publicKey;
        return data.publicKey;
      }
    }
  } catch (err) {
    console.error('[Push] Failed to fetch VAPID key:', err);
  }
  return null;
}

// ─── Timeout wrapper for Notification.requestPermission ──────
function requestPermissionWithTimeout(ms = 15000) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('default'), ms);
    Notification.requestPermission().then((result) => {
      clearTimeout(timer);
      resolve(result);
    }).catch(() => {
      clearTimeout(timer);
      resolve('denied');
    });
  });
}

// ─── Hook ────────────────────────────────────────────────────
export function usePushNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [swReady, setSwReady] = useState(false);
  const [supported, setSupported] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Check support + existing subscription on mount
  useEffect(() => {
    const isSupported = typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
    setSupported(isSupported);
    if (!isSupported) return;

    setPermission(Notification.permission);

    ensureServiceWorker().then((reg) => {
      if (!mountedRef.current) return;
      setSwReady(!!reg);
      if (reg) {
        reg.pushManager.getSubscription().then((sub) => {
          if (mountedRef.current) setSubscribed(!!sub);
        }).catch(() => {});
      }
    });
  }, []);

  // Canonical re-sync: read truth from the browser's PushManager
  const resyncFromBrowser = useCallback(async () => {
    try {
      const reg = await ensureServiceWorker();
      if (!reg) return false;
      const sub = await reg.pushManager.getSubscription();
      const isSubbed = !!sub;
      if (mountedRef.current) setSubscribed(isSubbed);
      return isSubbed;
    } catch (_) {
      return false;
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) { setError('unsupported'); return false; }
    setLoading(true);
    setError(null);

    let createdSubscription = null;

    try {
      // 1. Get VAPID key (from env or backend)
      const vapidKey = await fetchVapidKey();
      if (!vapidKey) {
        console.error('[Push] No VAPID public key available');
        if (mountedRef.current) { setError('config'); setLoading(false); }
        return false;
      }

      // 2. Request permission (with timeout guard)
      const perm = await requestPermissionWithTimeout(15000);
      if (mountedRef.current) setPermission(perm);

      if (perm === 'denied') {
        if (mountedRef.current) { setError('denied'); setLoading(false); }
        return false;
      }
      if (perm !== 'granted') {
        if (mountedRef.current) { setError('dismissed'); setLoading(false); }
        return false;
      }

      // 3. Service worker
      const reg = await ensureServiceWorker();
      if (!reg) {
        if (mountedRef.current) { setError('sw_failed'); setLoading(false); }
        return false;
      }

      // 4. Unsubscribe old
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      // 5. Subscribe to PushManager
      createdSubscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      // 6. Save to backend
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ subscription: createdSubscription.toJSON() }),
      });

      if (resp.ok) {
        // Re-sync from browser to confirm canonical state
        await resyncFromBrowser();
        if (mountedRef.current) setLoading(false);
        return true;
      }

      // Backend failed → rollback browser subscription to keep states in sync
      try { await createdSubscription.unsubscribe(); } catch (_) {}
      await resyncFromBrowser();
      if (mountedRef.current) { setError('backend'); setLoading(false); }
      return false;
    } catch (err) {
      console.error('[Push] subscribe error:', err);
      // Rollback any orphaned browser subscription
      if (createdSubscription) {
        try { await createdSubscription.unsubscribe(); } catch (_) {}
      }
      await resyncFromBrowser();
      if (mountedRef.current) { setError('unknown'); setLoading(false); }
      return false;
    }
  }, [supported, resyncFromBrowser]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      const reg = await ensureServiceWorker();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();
          const token = localStorage.getItem('token');
          await fetch(`${API}/push/unsubscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ endpoint }),
          });
        }
      }
    } catch (err) {
      console.error('[Push] unsubscribe error:', err);
    }
    // Re-sync from browser regardless of backend outcome
    await resyncFromBrowser();
    if (mountedRef.current) setLoading(false);
  }, [supported, resyncFromBrowser]);

  return { supported, permission, subscribed, loading, swReady, error, subscribe, unsubscribe, refresh: resyncFromBrowser };
}
